import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { fmtDate } from '@dcc-erp/shared';
import type { ApBillApprovalRow, ApPaymentRun } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HttpError } from '../../lib/api';
import {
  useApPaymentRun,
  useApproveRunStep,
  useRejectRunStep,
  useReturnRunStep,
  useSubmitApPaymentRun,
} from '../../lib/queries/ap';
import { AppModal } from '../AppModal';
import { ApprovalTimeline } from '../approval/ApprovalTimeline';
import { DecisionModal, type Decision } from '../approval/DecisionModal';
import { Can } from '../Can';

interface Props {
  companyId: string;
  /** The list row that opened this panel — used for the header before the full
   *  detail (with the approvals chain) resolves. */
  run: ApPaymentRun;
  onClose: () => void;
}

// PaymentRunApprovalPanel — the engine approval shell for a cross-vendor run.
// Near-copy of ApBillApprovalPanel: reuses AppModal + ApprovalTimeline +
// DecisionModal + Can, driven by useApPaymentRun + the run engine hooks. The
// maker submits (appayment.run); the approver pool decides (appayment.approve).
export function PaymentRunApprovalPanel({ companyId, run, onClose }: Props) {
  // Re-fetch the full detail so we have the materialized approvals chain even
  // when the opener only had a list row. Falls back to the passed-in run.
  const { data: detail } = useApPaymentRun(companyId, run.id);
  const view = detail ?? run;

  const submitMut = useSubmitApPaymentRun(companyId);
  const approveMut = useApproveRunStep(companyId);
  const rejectMut = useRejectRunStep(companyId);
  const returnMut = useReturnRunStep(companyId);

  const [showDecide, setShowDecide] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const qc = useQueryClient();

  const decideBusy = approveMut.isPending || rejectMut.isPending || returnMut.isPending;
  const submitBusy = submitMut.isPending;

  const approvalStatus = view.approvalStatus ?? null;
  const currentStep = view.currentStep ?? 0;
  // The detail (ApPaymentRunWithDetail) carries the materialized chain; the
  // header-only fallback (ApPaymentRun) does not, so default to an empty list.
  const approvals: ApBillApprovalRow[] =
    'approvals' in view && Array.isArray(view.approvals) ? view.approvals : [];
  const totalSteps = approvals.length;
  const isPending = approvalStatus === 'PENDING';
  const isResubmittable = approvalStatus === 'DRAFT' || approvalStatus === 'RETURNED';

  async function handleSubmit() {
    setActionError(null);
    try {
      await submitMut.mutateAsync(view.id);
      onClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function handleDecide(d: Decision) {
    setActionError(null);
    try {
      if (d.action === 'approve') {
        await approveMut.mutateAsync({
          id: view.id,
          step: d.step,
          comment: d.comment,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else if (d.action === 'return') {
        await returnMut.mutateAsync({
          id: view.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else {
        await rejectMut.mutateAsync({
          id: view.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      }
      setShowDecide(false);
      onClose();
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) {
        setActionError('รายการนี้เปลี่ยนสถานะแล้ว — รีเฟรชข้อมูลให้แล้ว');
        void qc.invalidateQueries({ queryKey: ['ap-payment-runs', companyId] });
      } else {
        setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
      }
    }
  }

  return (
    <AppModal
      title={`จ่ายเป็นชุด — ${view.voucherNo}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          {isResubmittable && (
            <Can permission="appayment.run">
              <button
                className="btn primary"
                disabled={submitBusy}
                onClick={() => {
                  void handleSubmit();
                }}
              >
                {submitBusy ? 'กำลังส่ง…' : 'ส่งอนุมัติ'}
              </button>
            </Can>
          )}
          {isPending && (
            <Can permission="appayment.approve">
              <button
                className="btn primary"
                disabled={decideBusy}
                onClick={() => setShowDecide(true)}
              >
                พิจารณา
              </button>
            </Can>
          )}
        </>
      }
    >
      {/* RETURNED reason banner (resubmit context) */}
      {approvalStatus === 'RETURNED' && view.returnedReason && (
        <div
          style={{
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning)',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 12,
          }}
          data-testid="run-returned-reason"
        >
          <strong style={{ color: 'var(--warning)' }}>เหตุผลที่ส่งกลับ:</strong>{' '}
          {view.returnedReason}
        </div>
      )}

      {isPending && (
        <div className="meta" style={{ marginBottom: 12 }}>
          <Icon name="check" size={12} /> รออนุมัติ · ขั้น {currentStep}/{totalSteps}
        </div>
      )}

      <ApprovalTimeline
        approvals={approvals}
        isActive={isPending}
        currentStep={currentStep}
        emptyText="ยังไม่ส่งอนุมัติ"
        leadLine={
          view.submittedBy ? (
            <div className="meta">
              ส่งอนุมัติโดย {view.submittedBy}
              {view.submittedAt ? ` · ${fmtDate(view.submittedAt)}` : ''}
            </div>
          ) : undefined
        }
        trailLine={
          view.status === 'APPROVED' && view.approvedBy ? (
            <div className="meta">
              อนุมัติโดย {view.approvedBy}
              {view.approvedAt ? ` · ${fmtDate(view.approvedAt)}` : ''}
            </div>
          ) : undefined
        }
      />

      {actionError && (
        <div
          style={{ color: 'var(--negative)', fontSize: 14, marginTop: 10 }}
          data-testid="run-action-error"
        >
          {actionError}
        </div>
      )}

      {showDecide && (
        <DecisionModal
          voucherNo={view.voucherNo}
          step={currentStep || 1}
          busy={decideBusy}
          error={actionError}
          titlePrefix="พิจารณาการจ่ายเป็นชุด"
          onConfirm={(d) => {
            void handleDecide(d);
          }}
          onClose={() => {
            setShowDecide(false);
            setActionError(null);
          }}
        />
      )}
    </AppModal>
  );
}
